import { CommonModule, SlicePipe } from "@angular/common";
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, OnInit, signal } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { MatExpansionModule } from "@angular/material/expansion";
import { MatIconModule } from "@angular/material/icon";
import { MatToolbarModule } from "@angular/material/toolbar";
import { RouterLink } from "@angular/router";
import { firstValueFrom } from "rxjs";
import { RedesSociales } from "@src/app/models/interfaces/redes.interface";
import { ConfigService } from "src/app/servicios/config/config.service";
import { PlatformService } from "src/app/servicios/platform/platform.service";
import { KebabCasePipe } from "../../pipes/kebab-case.pipe";
import { NoticiasService } from "../../servicios/noticias/noticias.service";
import { PublicidadService } from "../../servicios/publicidad/publicidad.service";
import { SidenavService } from "../../servicios/sidenav/sidenav.service";
import { ModalPreciosComponent } from "../modal-precios/modal-precios.component";
import { IconCloseComponent } from "../../icon-close/icon-close.component";
import { PortadaService } from "@src/app/servicios/portada/portada.service";
import { Subcanal } from "@src/app/models/interfaces/canal.interface";

@Component({
  selector: "app-sidenav-menu-burguer",
  templateUrl: "./sidenav-menu-burguer.component.html",
  styleUrls: ["./sidenav-menu-burguer.component.css"],
  imports: [CommonModule, MatToolbarModule, RouterLink, MatIconModule, MatExpansionModule, SlicePipe, KebabCasePipe, IconCloseComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SidenavMenuBurguerComponent implements OnInit {
  canales = [];
  subcanales: Subcanal[] = [];
  ultimosVideos = [];
  videosMasVisto = [];
  videosDirecto = [];
  redes: RedesSociales = {};
  publicidadSidebar: any;
  publicidadSidebarMobile: any;
  constantesGlobales: any;
  isWebView = false;
  selectorName;
  configBloque;
  showBloque = false;
  showApps = false;
  showAppAndroid = false;
  urlAppAndroid: string;
  imagenAppAndroid: any;
  showAppIos = false;
  urlAppIos: string;
  imagenAppIos: any;
  mostrarLogo = false;
  mostrarTiempo = false;
  mostrarRedes = false;
  mostrarBlogs = false;
  mostrarRevista = false;
  mostrarSecciones = false;
  mostrarTienda = false;
  mostrarMasVisto = false;
  subenlaces = false;
  isOpenAnimation = signal(false);
  isOpen = signal(false);

  constructor(
    private readonly sidenavService: SidenavService,
    private readonly noticiasService: NoticiasService,
    private readonly portadaService: PortadaService,
    private readonly cd: ChangeDetectorRef,
    private readonly configService: ConfigService,
    public readonly dialog: MatDialog,
    private readonly publicidadService: PublicidadService,
    private readonly elementRef: ElementRef,
    private readonly platformService: PlatformService,
  ) {}

  /**
   * Método de inicialización del componente que se ejecuta al cargar el mismo.
   *
   * - Obtiene las configuraciones globales y específicas del componente desde el servicio `ConfigService`.
   * - Configura las propiedades del componente basándose en las configuraciones obtenidas, como la visibilidad de bloques,
   *   enlaces, aplicaciones móviles, redes sociales, blogs, revista, secciones, tienda, y más.
   * - Recupera y filtra los subcanales activos desde el servicio `PortadaService`.
   * - Obtiene los videos más vistos desde el servicio `NoticiasService` y los almacena en una lista limitada a 7 elementos.
   * - Recupera la publicidad desde el servicio `PublicidadService` y selecciona aleatoriamente una publicidad para mostrar
   *   en el menú y en su versión móvil.
   * - Determina si el componente se está ejecutando dentro de un WebView de Android o iOS.
   * - Escucha los cambios en el estado del menú lateral (`sidenav`) a través del servicio `SidenavService` y abre o cierra
   *   el menú según corresponda.
   *
   * @returns {void}
   */
  ngOnInit(): void {
    this.configService.getConstantesGlobales();
    this.selectorName = this.elementRef.nativeElement.tagName.toLowerCase();
    const configPrensa = this.configService.getConfigPrensa();
    if (configPrensa) {
      firstValueFrom(configPrensa)
        .then((resultConfig) => {
          if (resultConfig) {
            this.configBloque = resultConfig.general[this.selectorName];
            this.showBloque = this.configBloque.bloque_activo === "true";
            if (this.showBloque) {
              this.showAppAndroid = this.configBloque.mostarDescargaAndroid === "true";
              this.urlAppAndroid = this.configBloque.urlAppAndroid;
              this.imagenAppAndroid = this.configBloque.imagenAppAndroid;
              this.showAppIos = this.configBloque.mostrarDescargaIos === "true";
              this.urlAppIos = this.configBloque.urlAppIos;
              this.imagenAppIos = this.configBloque.imagenAppIos;
              this.showApps = this.showAppAndroid || this.showAppIos;
              this.mostrarLogo = this.configBloque.mostrarLogo === "true";
              this.mostrarTiempo = this.configBloque.mostrarTiempo === "true";
              this.mostrarRedes = this.configBloque.mostrarRedes === "true";
              this.mostrarBlogs = this.configBloque.mostrarBlogs === "true";
              this.mostrarRevista = this.configBloque.mostrar_revista === "true";
              this.mostrarSecciones = this.configBloque.mostrarSecciones === "true";
              this.mostrarTienda = this.configBloque.mostrarTienda === "true";
              this.mostrarMasVisto = this.configBloque.mostrarMasVisto === "true";
              this.subenlaces = this.configBloque.subenlaces === "true";
            }

            this.redes = resultConfig.general["app-footer"];
          }
        })
        .catch((error) => {
          console.error("Error fetching configPrensa:", error);
        });
    }

    this.portadaService.getCanal().subscribe((canales) => {
      this.subcanales = canales[0].subcanales?.filter((subcanal) => subcanal.activoPrensaMenuLateral == "1");
      this.cd.detectChanges();
    });

    const mostWatchVideo = this.noticiasService.getVideosMasVistos();
    if (mostWatchVideo) {
      firstValueFrom(mostWatchVideo)
        .then((result) => {
          this.videosMasVisto = result?.slice(0, 7);
        })
        .catch((error) => {
          console.error("Error fetching most watched videos:", error);
        });
    }

    const publi = this.publicidadService.getPubli();
    if (publi) {
      firstValueFrom(publi)
        .then((result) => {
          if (result != null) {
            const arrayPublicidadCabecera = result.imagenes.filter((item) => item.nombrePosicion === "Menu");
            const numPubli = Math.floor(Math.random() * arrayPublicidadCabecera.length);

            const arrayPublicidadCabeceraMobile = result.imagenes.filter((item) => item.nombrePosicion === "AMP - menu");
            const numPubliMobile = Math.floor(Math.random() * arrayPublicidadCabeceraMobile.length);

            this.publicidadSidebar = arrayPublicidadCabecera[numPubli];
            this.publicidadSidebarMobile = arrayPublicidadCabeceraMobile[numPubliMobile];
          }
        })
        .catch((error) => {
          console.error("Error fetching publicidad:", error);
        });
    }

    this.isWebView = this.isInAndroidWebView() || this.isInIOSWebView();
    this.sidenavService.isOpenSubject.subscribe((isOpen: boolean) => {
      if (isOpen) {
        this.openSidenav();
      } else {
        this.closeSidenav();
      }
    });
  }

  /**
   * Verifica si la aplicación se está ejecutando dentro de un WebView en un dispositivo Android.
   *
   * @returns {boolean} `true` si el entorno es un WebView de Android, de lo contrario `false`.
   */
  isInAndroidWebView(): boolean {
    if (this.platformService.isBrowser()) {
      const userAgent = window.navigator.userAgent.toLowerCase();
      return /; wv/.test(userAgent);
    }
  }

  /**
   * Determina si la aplicación se está ejecutando en una WebView de iOS.
   *
   * Este método verifica si el entorno actual es un navegador y luego analiza
   * el agente de usuario para determinar si el dispositivo es un iPhone, iPod o iPad.
   * También comprueba si el navegador no es Safari y si no está en modo "standalone".
   *
   * @returns {boolean} `true` si la aplicación se ejecuta en una WebView de iOS,
   *                    de lo contrario, `false`.
   */
  isInIOSWebView(): boolean {
    if (this.platformService.isBrowser()) {
      const userAgent = window.navigator.userAgent.toLowerCase();

      const isIOS = /iphone|ipod|ipad/.test(userAgent);
      const notSafari = !/safari/.test(userAgent);
      const notStandalone = !("standalone" in window.navigator && window.navigator["standalone"]);

      return isIOS && notSafari && notStandalone;
    }
  }

  /**
   * Abre el menú lateral (sidenav) estableciendo los estados necesarios.
   *
   * - Cambia el estado `isOpen` a `true` para indicar que el menú está abierto.
   * - Después de un breve retraso, cambia el estado `isOpenAnimation` a `true`
   *   para iniciar la animación de apertura.
   *
   * @returns {void} No retorna ningún valor.
   */
  openSidenav(): void {
    this.isOpen.set(true);
    setTimeout(() => {
      this.isOpenAnimation.set(true);
    });
  }

  /**
   * Cierra el menú lateral (sidenav) ejecutando una animación de cierre.
   *
   * Este método realiza los siguientes pasos:
   * 1. Cambia el estado de `isOpenAnimation` a `false` para iniciar la animación de cierre.
   * 2. Después de un retraso de 300 ms (duración de la animación), cambia el estado de `isOpen` a `false`
   *    para ocultar completamente el menú lateral.
   */
  closeSidenav(): void {
    this.isOpenAnimation.set(false);
    setTimeout(() => {
      this.isOpen.set(false);
    }, 300);
  }

  /**
   * Abre un cuadro de diálogo modal utilizando el componente `ModalPreciosComponent`.
   *
   * @remarks
   * Este método utiliza el servicio de diálogo para abrir un modal con una clase de panel personalizada
   * denominada `modal-precios`. Después de que el diálogo se cierra, se suscribe a un observable
   * para realizar acciones adicionales si es necesario.
   *
   * @example
   * ```typescript
   * this.openDialog();
   * ```
   */
  openDialog() {
    const dialogRef = this.dialog.open(ModalPreciosComponent, { panelClass: "modal-precios" });
    dialogRef.afterClosed().subscribe(() => {});
  }
}
